import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {environment} from '@environment';
import {BankDetails} from '@portal/models/bank-details';
import {CalculateTransferFeeResult} from '@portal/models/calculate-transfer-fee-result';
import {Contract} from '@portal/models/contract';
import {Date} from '@portal/models/date';
import {Dealer} from '@portal/models/dealer';
import {DelayOfPayment} from '@portal/models/delay-of-payment';
import {EarlyTerminationResult} from '@portal/models/early-termination-result';
import {PersonalData} from '@portal/models/personal-data';
import {BankConnectionChangeRequest} from '@portal/models/request/bank-connection-change-request';
import {CalculateTransferFeeRequest} from '@portal/models/request/calculate-transfer-fee-request';
import {CaseOfDamageRequest} from '@portal/models/request/case-of-damage-request';
import {DefermentOfPaymentRequest} from '@portal/models/request/deferment-of-payment-request';
import {EarlyTerminationRequest} from '@portal/models/request/early-termination-request';
import {PaymentDateChangeRequest} from '@portal/models/request/payment-date-change-request';
import {SpecialRepaymentRequest} from '@portal/models/request/special-repayment-request';
import {VehicleLetterShipmentRequest} from '@portal/models/request/vehicle-letter-shipment-request';
import {RsvLrvInfo} from '@portal/models/rsv-lrv-info';
import {SepaBankDetails} from '@portal/models/sepa-bank-details';
import {Vehicle} from '@portal/models/vehicle';
import {forkJoin, lastValueFrom, map, Observable, of, shareReplay, tap} from 'rxjs';
import {parseResponse} from 'src/app/utils/parse-response.operator';
import urlcat from 'src/app/utils/urlcat';
import {z} from 'zod';

@Injectable({
  providedIn: 'root',
})
export class ContractService {
  private contracts$?: Observable<Contract[]>;
  private caseOfDamageCache = new Map<string, Observable<Partial<Contract>>>();
  private vehicleLetterCache = new Map<string, Observable<Partial<Contract>>>();
  private changePaymentCache = new Map<string, Observable<Partial<Contract>>>();
  private bankDetailsCache = new Map<string, Observable<BankDetails>>();

  constructor(private http: HttpClient) {}

  /**
   * Load all contracts of the current user and setting contracts
   */
  public loadContracts(): Observable<Contract[]> {
    if (!this.contracts$) {
      this.contracts$ = this.http.get<Contract[]>(environment.endpoints.contracts).pipe(parseResponse(z.array(Contract)), shareReplay(1));
    }

    return this.contracts$;
  }

  public loadContractInfoData(contractNumber: string): Observable<Partial<Contract>> {
    const url = urlcat(environment.endpoints.contractInfoData, {contractNumber});
    return this.http.get<Partial<Contract>>(url).pipe(parseResponse(Contract.partial()));
  }

  public loadAllCaseOfDamageData(contracts: Contract[]) {
    return this.loadAllOfPartialContractsData(contracts, environment.endpoints.allCaseOfDamageData, this.caseOfDamageCache);
  }

  public loadCaseOfDamageData(contractNumber: string): Observable<Partial<Contract>> {
    const cached = this.caseOfDamageCache.get(contractNumber);
    if (cached) {
      return cached;
    }
    const url = urlcat(environment.endpoints.caseOfDamageData, {contractNumber});
    const request$ = this.http.get<Partial<Contract>>(url).pipe(parseResponse(Contract.partial()), shareReplay(1));
    this.caseOfDamageCache.set(contractNumber, request$);
    return request$;
  }

  public loadAllVehicleLetterData(contracts: Contract[]) {
    return this.loadAllOfPartialContractsData(contracts, environment.endpoints.allVehicleLetterData, this.vehicleLetterCache);
  }

  public loadVehicleLetterData(contractNumber: string): Observable<Partial<Contract>> {
    const cached = this.vehicleLetterCache.get(contractNumber);
    if (cached) {
      return cached;
    }
    const url = urlcat(environment.endpoints.vehicleLetterData, {contractNumber});
    const request$ = this.http.get<Partial<Contract>>(url).pipe(parseResponse(Contract.partial()));
    this.vehicleLetterCache.set(contractNumber, request$);
    return request$;
  }

  public loadSpecialRepaymentData(contractNumber: string) {
    const url = urlcat(environment.endpoints.specialRepaymentData, {contractNumber});
    return this.http.get<Partial<Contract>>(url).pipe(parseResponse(Contract.partial()));
  }

  public loadDelayOfPaymentData(contractNumber: string) {
    const url = urlcat(environment.endpoints.delayOfPaymentData, {contractNumber});
    return this.http.get<Partial<Contract>>(url).pipe(parseResponse(Contract.partial()));
  }

  public loadEarlyTerminationData(contractNumber: string) {
    const url = urlcat(environment.endpoints.earlyTerminationData, {contractNumber});
    return this.http.get<Partial<Contract>>(url).pipe(parseResponse(Contract.partial()));
  }

  public loadChangePaymentData(contracts: Contract[]) {
    return this.loadAllOfPartialContractsData(contracts, environment.endpoints.changeAllPaymentData, this.changePaymentCache);
  }

  public loadPaymentDataChangeData(contractNumber: string) {
    const url = urlcat(environment.endpoints.paymentDateChangeData, {contractNumber});
    return this.http.get<Partial<Contract>>(url).pipe(parseResponse(Contract.partial()));
  }

  /**
   * Load expiring contracts of the current user
   */
  public loadExpiringContracts(): Observable<string[]> {
    return this.http.get<string[]>(environment.endpoints.expiringContracts).pipe(parseResponse(z.array(z.string())));
  }

  /**
   * Mark expiry info of contracts read
   */
  public updateExpiringContracts(contractNumbers: string[]): Observable<string> {
    const options = {
      responseType: 'text' as 'json',
    };
    return this.http.post<string>(environment.endpoints.updateExpiringContracts, contractNumbers, options).pipe(parseResponse(z.string()));
  }

  /**
   * Request an early termination of a specific contract
   * @param request The request containing contract number and termination date
   */
  public requestEarlyTermination(request: EarlyTerminationRequest): Observable<EarlyTerminationResult> {
    return this.http
      .post<EarlyTerminationResult>(environment.endpoints.requestEarlyTermination, request)
      .pipe(parseResponse(EarlyTerminationResult));
  }

  /**
   * Generate a Info PDF for an early termination
   * @param contractNumber the contractNumber to generate the PDF for
   * @param terminationDate the termination date for the early termination
   */
  public downloadEarlyTerminationInfoPdf(contractNumber: string, terminationDate: Date): Observable<Blob> {
    const params = new HttpParams().set('contract', contractNumber).set('date', terminationDate.toISOString());
    const url = `${environment.endpoints.earlyTerminationInfoPdf}?${params.toString()}`;
    const options = {responseType: 'blob' as 'json'};

    return this.http.get<Blob>(url, options);
  }

  public downloadEarlyTerminationPdf(pdfDocumentId: string): Observable<Blob> {
    const url = `${environment.endpoints.earlyTerminationPdf}${pdfDocumentId}`;
    const options = {responseType: 'blob' as 'json'};

    return this.http.get<Blob>(url, options);
  }

  public downloadVehicleLetterInfoPdf(pdfFileName: string): Observable<Blob> {
    const params = new HttpParams().set('pdfFileName', pdfFileName);
    const url = `${environment.endpoints.vehicleLetterShipment}/pdf?${params.toString()}`;
    const options = {responseType: 'blob' as 'json'};

    return this.http.get<Blob>(url, options);
  }

    public downloadBankStatementPdf(contractNumber: string): Observable<Blob> {
    const url = `${environment.endpoints.getBankStatement}/${contractNumber}`;
    const options = {responseType: 'blob' as 'json'};

    return this.http.get<Blob>(url, options);
  }

  public downloadRedemptionPlanPdf(contractNumber: string): Observable<Blob> {
    const url = `${environment.endpoints.getRedemptionPlan}/${contractNumber}`;
    const options = {responseType: 'blob' as 'json'};

    return this.http.get<Blob>(url, options);
  }

  public checkForActiveVehicleLetterShipments(contractNumbers: string[]): Observable<string[]> {
    const params = new HttpParams().set('contractNumbers', contractNumbers.join(','));
    return this.http.get<string[]>(environment.endpoints.checkVehicleLetterShipments, {params}).pipe(parseResponse(z.array(z.string())));
  }

  /**
   * Calculate the transfer fee
   * @param request The request containing contract number and termination date
   * @returns Observable containing the calculation result
   */
  public calculateTransferFee(request: CalculateTransferFeeRequest): Observable<CalculateTransferFeeResult> {
    return this.http
      .post<CalculateTransferFeeResult>(environment.endpoints.calculateTransferFee, request)
      .pipe(parseResponse(CalculateTransferFeeResult));
  }

  /**
   * Get information about a specific bank by a given IBAN
   * @param iban The IBAN used to search for the associated bank
   */
  public getSepaBankDetailsByIban(iban: string): Observable<SepaBankDetails> {
    iban = iban.replace(/\s/g, '');
    return this.http.get<SepaBankDetails>(`${environment.endpoints.bankDetailsByIban}${iban}`).pipe(parseResponse(SepaBankDetails));
  }

  /**
   * Request a bank connection change
   * @param request Request containing the new bank connection
   */
  public requestBankConnectionChange(request: BankConnectionChangeRequest): Observable<void> {
    return this.http.post<void>(environment.endpoints.changeBankConnection, request);
  }

  public hasIbanChanged(iban: string, contractIds: number[]): Observable<boolean> {
    const ids = contractIds.join(',');
    const params = new HttpParams().set('iban', iban).set('contractIds', ids);
    return this.http
      .get<boolean>(environment.endpoints.hasIbanChanged, {
        params,
      })
      .pipe(parseResponse(z.boolean()));
  }

  public requestVehicleLetterShipment(request: VehicleLetterShipmentRequest): Observable<string> {
    const options = {
      responseType: 'text' as 'json',
    };
    return this.http.post<string>(environment.endpoints.vehicleLetterShipment, request, options).pipe(parseResponse(z.string()));
  }

  public newMandateNeeded(contractIds: number[]): Observable<boolean> {
    return this.http.post<boolean>(environment.endpoints.newMandateNeeded, contractIds).pipe(parseResponse(z.boolean()));
  }

  public generateSepaMandate(bankDetails: BankDetails, validFrom: Date, contractNumbers: string[]): Observable<string> {
    const options = {
      responseType: 'text' as 'json',
    };
    const body = {
      bankDetails,
      validFrom,
      contractNumbers,
    };

    return this.http.post<string>(environment.endpoints.generateSepaMandate, body, options).pipe(parseResponse(z.string()));
  }

  public downloadSepaMandatePdf(mandateFileName: string): Observable<Blob> {
    const params = new HttpParams().set('pdfFileName', mandateFileName);
    const url = `${environment.endpoints.downloadSepaMandate}?${params.toString()}`;
    const options = {responseType: 'blob' as 'json'};

    return this.http.get<Blob>(url, options);
  }

  public reportCaseOfDamage(caseOfDamageRequest: CaseOfDamageRequest): Observable<string> {
    const options = {
      responseType: 'text' as 'json',
    };
    return this.http.post<string>(environment.endpoints.reportCaseOfDamage, caseOfDamageRequest, options).pipe(parseResponse(z.string()));
  }

  public downloadRsvLrvPdf(contractNumber: string): Observable<Blob> {
    const params = new HttpParams().set('contractNumber', contractNumber);
    const url = `${environment.endpoints.getRsvLrvApplication}/pdf?${params.toString()}`;
    const options = {responseType: 'blob' as 'json'};

    return this.http.get<Blob>(url, options);
  }

  public downloadRsvLrvPlusPdf(contractNumber: string): Observable<Blob> {
    const params = new HttpParams().set('contractNumber', contractNumber);
    const url = `${environment.endpoints.getRsvLrvPlusApplication}/pdf?${params.toString()}`;
    const options = {responseType: 'blob' as 'json'};

    return this.http.get<Blob>(url, options);
  }

  /**
   * Get all possible termination dates for a specific contract
   * @param contractNumber The contract number of the contract to get the termination dates for
   */
  public getTerminationDates(contractNumber: string): Observable<Date[]> {
    const params = new HttpParams().set('contractNumber', contractNumber);
    const url = `${environment.endpoints.getTerminationDates}?${params.toString()}`;
    return this.http.get<Date[]>(url).pipe(parseResponse(z.array(Date)));
  }

  /**
   * Get the vehicle for a specific contract
   * @param contractNumber The contract number of the contract to get the vehicle for
   */
  public getDealer(contractNumber: string): Observable<Dealer> {
    const params = new HttpParams().set('contractNumber', contractNumber);
    const url = `${environment.endpoints.getDealer}?${params.toString()}`;
    return this.http.get<Dealer>(url).pipe(parseResponse(Dealer.nullable()));
  }

  public getBankDetails(contractNumber: string): Observable<BankDetails> {
    const params = new HttpParams().set('contractNumber', contractNumber);
    const url = `${environment.endpoints.getBankDetails}?${params.toString()}`;
    return this.http.get<BankDetails>(url).pipe(parseResponse(BankDetails.nullable()));
  }

  public getVehicle(contractNumber: string): Observable<Vehicle> {
    const params = new HttpParams().set('contractNumber', contractNumber);
    const url = `${environment.endpoints.getVehicle}?${params.toString()}`;
    return this.http.get<Vehicle>(url).pipe(parseResponse(Vehicle.nullable()));
  }

  public getContractor(contractNumber: string): Observable<PersonalData> {
    const params = new HttpParams().set('contractNumber', contractNumber);
    const url = `${environment.endpoints.getContractor}?${params.toString()}`;
    return this.http.get<PersonalData>(url).pipe(parseResponse(PersonalData.nullable()));
  }

  public getAllBankDetails(contractNumbers: string[]): Observable<BankDetails[]> {
    return this.loadAllBankData(contractNumbers, environment.endpoints.getAllBankDetails, this.bankDetailsCache);
  }

  public getAllVehicles(contractNumbers: string[]): Observable<Map<string, Vehicle>> {
    const params = new HttpParams().set('contractNumbers', contractNumbers.join(','));
    return this.http
      .get<Map<string, Vehicle>>(environment.endpoints.getAllVehicles, {params})
      .pipe(parseResponse(z.map(z.string(), Vehicle)));
  }

  public getAllContractors(contractNumbers: string[]): Observable<Map<string, PersonalData>> {
    const params = new HttpParams().set('contractNumbers', contractNumbers.join(','));
    return this.http
      .get<Map<string, PersonalData>>(environment.endpoints.getAllContractors, {
        params,
      })
      .pipe(parseResponse(z.map(z.string(), PersonalData)));
  }
  
  public getVehicleLetterFee(): Observable<number> {
    return this.http.get<number>(environment.endpoints.getVehicleLetterFee).pipe(parseResponse(z.number()));
  }

  public requestDefermentOfPayment(request: DefermentOfPaymentRequest): Observable<DelayOfPayment> {
    return this.http.post<DelayOfPayment>(environment.endpoints.requestDefermentOfPayment, request).pipe(parseResponse(DelayOfPayment));
  }

  public calculateDefermentOfPayment(request: DefermentOfPaymentRequest): Observable<DelayOfPayment> {
    return this.http.post<DelayOfPayment>(environment.endpoints.calculateDefermentOfPayment, request).pipe(parseResponse(DelayOfPayment));
  }

  public requestPaymentDateChange(request: PaymentDateChangeRequest): Observable<void> {
    return this.http.post<void>(environment.endpoints.requestPaymentDateChange, request);
  }

  public requestSpecialRepayment(request: SpecialRepaymentRequest): Observable<void> {
    return this.http.post<void>(environment.endpoints.specialRepayment, request);
  }

  public getSpecialRepaymentFee(contractNumber: string): Observable<number> {
    const params = new HttpParams().set('contractNumber', contractNumber);
    return this.http.get<number>(environment.endpoints.getSpecialRepaymentFee, {params}).pipe(parseResponse(z.number()));
  }

  public showReportingAU(): Observable<RsvLrvInfo> {
    return this.http.get<RsvLrvInfo>(environment.endpoints.showReportingAU).pipe(parseResponse(RsvLrvInfo));
  }

  resetAll() {
    this.resetContracts();
    this.caseOfDamageCache.clear();
    this.vehicleLetterCache.clear();
    this.changePaymentCache.clear();
    this.bankDetailsCache.clear();
  }

  resetContracts() {
    this.contracts$ = undefined;
  }

  private loadAllBankData(contractNumbers: string[], url: string, cache: Map<string, Observable<BankDetails>>): Observable<BankDetails[]> {
    const uniqueContractNumbers = Array.from(new Set(contractNumbers)).sort();
    let missingContracts: string[] = [];
    const cachedData$: Observable<BankDetails>[] = [];
    for (const contractNumber of uniqueContractNumbers) {
      const cached = cache.get(contractNumber);
      if (cached) {
        cachedData$.push(cached);
      } else {
        missingContracts.push(contractNumber);
      }
    }
    if (missingContracts.length === 0) {
      return this.combineResults<BankDetails>(cachedData$, of([]), missingContracts);
    }
    const chunks = this.chunkArray(missingContracts, 25);
    const request$ = chunks.map(contractNumberChunks => {
      const params = new HttpParams().set('contractNumbers', contractNumberChunks.join(','));

      return this.http.get<BankDetails[]>(url, {params}).pipe(
        parseResponse(z.array(BankDetails)),
        tap(results => {
          for (let i = 0; i < contractNumberChunks.length; i++) {
            const item$ = of(results[i]).pipe(shareReplay(1));
            cache.set(contractNumberChunks[i], item$);
          }
        }),
        shareReplay(1),
      );
    });
    const newData$ = forkJoin(request$).pipe(map(resultArrays => resultArrays.flat()));
    return this.combineResults<BankDetails>(cachedData$, newData$, missingContracts);
  }

  private loadAllOfPartialContractsData(
    contracts: Contract[],
    url: string,
    cache: Map<string, Observable<Partial<Contract>>>,
  ): Observable<Partial<Contract>[]> {
    const uniqueContractNumbers = Array.from(new Set(contracts.map(contracts => contracts.contractNumber))).sort();
    let missingContracts: string[] = [];
    const cachedData$: Observable<Partial<Contract>>[] = [];
    for (const contractNumber of uniqueContractNumbers) {
      const cached = cache.get(contractNumber);
      if (cached) {
        cachedData$.push(cached);
      } else {
        missingContracts.push(contractNumber);
      }
    }
    if (missingContracts.length === 0) {
      return this.combineResults<Partial<Contract>>(cachedData$, of([]), missingContracts);
    }
    const chunks = this.chunkArray(missingContracts, 25);
    const request$ = chunks.map(contractNumberChunks => {
      const params = new HttpParams().set('contractNumbers', contractNumberChunks.join(','));

      return this.http.get<Partial<Contract>[]>(url, {params}).pipe(
        parseResponse(z.array(Contract.partial())),
        tap(results => {
          for (const item of results) {
            if (item?.contractNumber) {
              const item$ = of(item).pipe(shareReplay(1));
              cache.set(item.contractNumber, item$);
            }
          }
        }),
        shareReplay(1),
      );
    });
    const newData$ = forkJoin(request$).pipe(map(resultArrays => resultArrays.flat()));
    return this.combineResults<Partial<Contract>>(cachedData$, newData$, missingContracts);
  }

  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  private combineResults<T>(cachedData$: Observable<T>[], newData$: Observable<T[]>, missingKeys: string[]): Observable<T[]> {
    if (cachedData$.length === 0 && missingKeys.length > 0) {
      // only new data
      return newData$;
    } else if (cachedData$.length > 0 && missingKeys.length === 0) {
      // only cached data
      return forkJoin(cachedData$);
    } else if (cachedData$.length > 0 && missingKeys.length > 0) {
      // both cached and new data
      return forkJoin([
        forkJoin(cachedData$), // Observable<T[]>
        newData$, // Observable<T[]>
      ]).pipe(map(([cached, fresh]) => [...cached, ...fresh]));
    } else {
      // nothing to fetch
      return of([]);
    }
  }
}
